import { DoubleSide, Group, Mesh, MeshBasicMaterial, PlaneGeometry } from 'three'
import { Setting } from '@/plugins/cad/module/common/setting'
import type { CADController } from '../CADController'
import type { CADViewManager } from '../manager/CADViewManager'
import type { EntityGrip } from './entityGripHandler'

/**
 * 实体控制点覆盖层，负责在临时 Group 中绘制并维护控制点 mesh。
 */
export class EntityGripOverlay {
  private readonly group = new Group()
  private readonly geometry = new PlaneGeometry(2, 2)
  private readonly gripMeshMap = new Map<string, Mesh>()
  private hoverGripId: string | null = null
  private activeGripId: string | null = null

  constructor(
    private readonly controller: CADController,
    private readonly viewManager: CADViewManager
  ) {
    this.viewManager.attachTempObject(this.group)
  }

  /**
   * 重新设置需要显示的控制点集合。
   *
   * @param grips - 控制点列表
   */
  setGrips(grips: EntityGrip[]) {
    this.disposeMeshes()

    for (const grip of grips) {
      const material = this.createMaterial(grip.id)
      const mesh = new Mesh(this.geometry, material)
      mesh.position.set(grip.point.x, grip.point.y, 0)
      mesh.rotation.z = grip.meta?.angle ?? 0
      mesh.userData.grip = grip
      this.group.add(mesh)
      this.gripMeshMap.set(grip.id, mesh)
    }

    this.updateVisualState()
    this.updateScale()
  }

  /**
   * 设置当前悬停的控制点。
   *
   * @param gripId - 悬停控制点 id，传 null 取消悬停
   */
  setHoverGrip(gripId: string | null) {
    this.hoverGripId = gripId
    this.updateVisualState()
  }

  /**
   * 设置当前激活（拖动中）的控制点。
   *
   * @param gripId - 激活控制点 id，传 null 取消激活
   */
  setActiveGrip(gripId: string | null) {
    this.activeGripId = gripId
    this.updateVisualState()
  }

  /** 清除所有控制点与悬停/激活状态 */
  clear() {
    this.hoverGripId = null
    this.activeGripId = null
    this.setGrips([])
  }

  /** 按当前相机像素大小更新控制点 mesh 的缩放 */
  updateScale() {
    const scale = this.controller.getPixelSizeInWorld(Setting.gripConfig.pixelRadius)
    this.gripMeshMap.forEach((mesh) => {
      const grip = mesh.userData.grip as EntityGrip | undefined
      const scaleX = grip?.meta?.scaleX ?? 1
      const scaleY = grip?.meta?.scaleY ?? 1
      mesh.scale.set(scale * scaleX, scale * scaleY, 1)
    })
  }

  /** 释放所有控制点 mesh、几何体与覆盖层 Group */
  dispose() {
    this.disposeMeshes()
    this.group.removeFromParent()
    this.geometry.dispose()
  }

  private updateVisualState() {
    this.gripMeshMap.forEach((mesh, gripId) => {
      const material = mesh.material as MeshBasicMaterial
      const isActive = gripId === this.activeGripId
      const isHover = gripId === this.hoverGripId
      material.color.setHex(
        isActive
          ? Setting.gripConfig.activeColor
          : isHover
            ? Setting.gripConfig.hoverColor
            : Setting.gripConfig.color
      )
      material.needsUpdate = true
    })
  }

  private createMaterial(gripId: string) {
    return new MeshBasicMaterial({
      color:
        gripId === this.activeGripId ? Setting.gripConfig.activeColor : Setting.gripConfig.color,
      depthTest: Setting.gripConfig.depthTest,
      depthWrite: Setting.gripConfig.depthWrite,
      side: DoubleSide,
    })
  }

  private disposeMeshes() {
    this.gripMeshMap.forEach((mesh) => {
      ;(mesh.material as MeshBasicMaterial).dispose()
      mesh.removeFromParent()
    })
    this.gripMeshMap.clear()
  }
}
